import React, { useCallback, useMemo, useContext, useEffect, useLayoutEffect, useState } from 'react'

/**
 * Material UI
 */
import { makeStyles } from '@material-ui/core/styles';
import { Badge, Box, IconButton, Tooltip, Typography, Icon } from '@material-ui/core';

/**
 * Slate
 */
import { Editable, withReact, useSlate, Slate } from 'slate-react'
import { Editor, Transforms, createEditor, Node } from 'slate'
import { withHistory } from 'slate-history'
import { CompactToolbar, StandardToolbar } from './controls';
import { withMedia, MediaElement, InsertMediaLinkButton, SelectMediaButton } from './plugins/Media';
import { withLinks, LinkElement, LinkButton } from './plugins/Links';
import { withVideos, VideoElement, InsertVideoButton } from './plugins/Videos';
import { withMathExpressions, MathExpressionElement, InsertMathExpressionButton, MathExpressionEditorDialog } from './plugins/MathExpression';

/**
 * QR Code
 */
import QRCode from 'qrcode.react';

/**
 * Miscellaneous
 */
import isHotkey from 'is-hotkey';

/**
 * Application
 */
import { UserContext } from '../../App';
import { PrintLayoutContext } from './hooks';

const HOTKEYS = {
    'mod+b': 'bold',
    'mod+i': 'italic',
    'mod+u': 'underline',
    'mod+`': 'code',
}

const LIST_TYPES = ['numbered-list', 'bulleted-list']

export const prepopulatedRichTextValue = (text) => {
    return (
        [{
            type: "paragraph",
            children: [{
                text: (text || "")
            }]
        }]
    );
}

/** Flatten Slate rich-text value to plain text (for AI prompts). */
export const extractPlainTextFromRichText = (richText) => {
    if (!richText || !Array.isArray(richText)) {
        return "";
    }
    return richText.map((node) => extractPlainTextFromBlock(node)).join("\n\n").trim();
}

function extractPlainTextFromBlock(node) {
    if (!node?.children) {
        return Node.string(node);
    }

    if (node.children.length === 1 && node.children[0].text != null && !node.children[0].breakBefore) {
        return node.children[0].text;
    }

    return node.children.map((child) => {
        const prefix = child.breakBefore ? '\n' : '';
        return `${prefix}${child.text || ''}`;
    }).join('');
}

/** Count words (English) or characters (Chinese) in rich-text content. */
export function countRichTextWords(richText, languageCode = 'en') {
    const plainText = extractPlainTextFromRichText(richText);
    if (!plainText) {
        return { count: 0, unit: languageCode === 'en' ? 'words' : '字' };
    }

    if (languageCode === 'en') {
        return {
            count: plainText.trim().split(/\s+/).filter(Boolean).length,
            unit: 'words',
        };
    }

    return {
        count: plainText.replace(/\s/g, '').length,
        unit: '字',
    };
}

export function formatRichTextWordCount(richText, languageCode = 'en') {
    const { count, unit } = countRichTextWords(richText, languageCode);
    return `${count} ${unit}`;
}

function parseInlineMarkdown(text) {
    const children = [];
    let index = 0;

    while (index < text.length) {
        if (text.startsWith('**', index)) {
            const end = text.indexOf('**', index + 2);
            if (end !== -1) {
                children.push({ text: text.slice(index + 2, end), bold: true });
                index = end + 2;
                continue;
            }
        }
        if (text.startsWith('__', index)) {
            const end = text.indexOf('__', index + 2);
            if (end !== -1) {
                children.push({ text: text.slice(index + 2, end), underline: true });
                index = end + 2;
                continue;
            }
        }
        if (text[index] === '*' && text[index + 1] !== '*') {
            const end = text.indexOf('*', index + 1);
            if (end !== -1) {
                children.push({ text: text.slice(index + 1, end), italic: true });
                index = end + 1;
                continue;
            }
        }

        const nextMarker = text.slice(index).search(/\*\*|__|\*/);
        const end = nextMarker === -1 ? text.length : index + nextMarker;
        if (end > index) {
            children.push({ text: text.slice(index, end) });
        }
        index = end === index ? index + 1 : end;
    }

    return children.length ? children : [{ text: '' }];
}

const PARAGRAPH_BLOCK_STYLE = {
    marginTop: 0,
    marginBottom: '1em',
    whiteSpace: 'pre-wrap',
};

function hasInlineMarkdown(text) {
    return /(\*\*|__|(^|[^*])\*[^*])/.test(text);
}

function paragraphFromMarkdownLine(line) {
    const trimmed = line.trim();
    if (trimmed.startsWith('# ')) {
        return { type: 'heading-one', children: parseInlineMarkdown(trimmed.slice(2)) };
    }
    if (trimmed.startsWith('## ')) {
        return { type: 'heading-two', children: parseInlineMarkdown(trimmed.slice(3)) };
    }
    return { type: 'paragraph', children: parseInlineMarkdown(trimmed) };
}

/** Build one Slate block from a paragraph chunk (may contain single \\n line breaks). */
function paragraphFromTextBlock(block) {
    const normalizedBlock = block.replace(/\r\n/g, '\n');
    if (!hasInlineMarkdown(normalizedBlock)) {
        return {
            type: 'paragraph',
            children: [{ text: normalizedBlock }],
        };
    }

    const lines = normalizedBlock.split('\n');
    if (lines.length <= 1) {
        return paragraphFromMarkdownLine(normalizedBlock.trim());
    }

    const children = [];
    lines.forEach((line, lineIndex) => {
        const parsed = parseInlineMarkdown(line.trim());
        parsed.forEach((child, childIndex) => {
            children.push({
                ...child,
                breakBefore: lineIndex > 0 && childIndex === 0,
            });
        });
    });

    return {
        type: 'paragraph',
        children: children.length ? children : [{ text: '' }],
    };
}

/** Convert plain text or lightweight markdown into a Slate document value. */
export const plainTextOrMarkdownToRichTextValue = (text) => {
    if (!text || typeof text !== 'string') {
        return prepopulatedRichTextValue('');
    }

    let cleaned = text
        .replace(/\r\n/g, '\n')
        .replace(/^```(?:json|markdown)?\s*\n?/gim, '')
        .replace(/\n?```\s*$/gim, '')
        .trim();

    if (!cleaned) {
        return prepopulatedRichTextValue('');
    }

    const paragraphBlocks = cleaned.split(/\n{2,}/).map((part) => part.trim()).filter(Boolean);
    return paragraphBlocks.map((block) => paragraphFromTextBlock(block));
}

/**
 * Extract a list of URLs for all media items in sequence
 */
export const extractMediaUrls = (value) => {
    let urls = [];
    value.forEach(item => {
        if (item.type === "audio") {
            urls.push(item.url);
        }
        if (Boolean(item.children)) {
            urls = urls.concat(extractMediaUrls(item.children));
        }
    });
    return urls;
}

/**
 * TODO: Include a Table plugin
 */
export const RichTextEditor = (props) => {
    const { account } = useContext(UserContext);
    const [showMenu, setShowMenu] = useState(true);
    const classes = makeStyles(theme => ({
        editorBox: {
            borderRadius: 4,
            borderColor: theme.palette.primary.main,
            borderStyle: "solid",
            borderWidth: 2,
            padding: theme.spacing(2),
        }
    }))();
    const { variant, mode = 'standard', readOnly, printLayout, setValue, additionalActions, className, showWordCount = false, wordCountLanguage = 'en', ...others } = props;
    const value = (typeof props.value === "string")
        ? prepopulatedRichTextValue(props.value) :
        props.value || prepopulatedRichTextValue("");
    const wordCountLabel = useMemo(
        () => formatRichTextWordCount(value, wordCountLanguage),
        [value, wordCountLanguage]
    );

    /**
     * Override Slate Editor Config
     */
    const renderElement = useCallback(props => {
        return <Element {...props} />
    }, [])
    const renderLeaf = useCallback(props => <Leaf {...props} />, [])
    // Embed loggedInUser into the editor object
    var editor = createEditor();
    editor.account = account;
    // Wrap the editor with all the various plugins
    editor = useMemo(() => withMathExpressions(
        withVideos(
            withLinks(
                withMedia(
                    withHistory(
                        withReact(editor)))))),
        []);

    /**
     * Insert empty paragraphy after non-paragraph node when hitting enter
     * TODO: Externalize
     */
    const { insertBreak, deleteBackward, isVoid, isInline } = editor;
    // editor.insertBreak = () => {
    //     const { selection } = editor;
    //     let parentNode = Node.parent(editor, selection.focus.path);
    //     if (Boolean(parentNode) && parentNode.type !== "paragraph") {
    //         Transforms.insertNodes(editor, [{
    //             type: "paragraph",
    //             children: [{
    //                 text: ""
    //             }]
    //         }])
    //     } else {
    //         insertBreak();
    //     }
    // }
    // editor.isVoid = (element) => {
    //     return (element.isVoid !== undefined) ? Boolean(element.isVoid) : isVoid(element);
    // }
    editor.isInline = (element) => {
        return (element.isInline !== undefined) ? Boolean(element.isInline) : isInline(element);
    }

    editor.isVoid = (element) => {
        return (element.type === 'break') ? true : isVoid(element);
    }
    editor.insertBreak = () => {
        let { path } = editor?.selection?.focus;
        let elementIndex = path[0] || 0;
        let targetCursor = {
            path: [elementIndex + 1, 0],
            offset: 0
        }
        // let nextNode = Editor.next(editor);
        // let currentNode = Editor.node(editor, path);
        // Transforms.setNodes(editor, { break: true }, { at: path });
        Transforms.insertNodes(editor, [
            // { type: 'break', children: [{ text: '' }] },
            { type: 'paragraph', children: [{ text: '', breakBefore: true }] }
        ]);
        Transforms.select(editor, {
            anchor: targetCursor,
            focus: targetCursor
        })
    }

    // TODO: How to move it into the plugin?
    const mediaUrls = extractMediaUrls(value);
    editor.mediaUrls = mediaUrls;

    return (
        <PrintLayoutContext.Provider value={printLayout}>
            {/* TODO: To Remove - Debug Message */}
            {false && Boolean(process.env.NODE_ENV === 'development') &&
                <React.Fragment>
                    <Box style={{ whiteSpace: 'pre-wrap' }}>
                        Value: {JSON.stringify(value, null, '\t')}
                    </Box>
                    <Box>
                        At: {JSON.stringify(editor?.selection, null, '\t')}
                    </Box>
                </React.Fragment>}
            {/* End: TODO: To Remove - Debug Message */}
            <Box className={className || (variant === "outlined" ? classes.editorBox : null)} style={{ position: 'relative' }} {...others}>
                <Slate editor={editor} value={value} onChange={v => setValue(v)}>
                    {/* Standard Toolbar - Top */}
                    {!Boolean(readOnly) && Boolean(mode === 'standard') &&
                        <Box display="flex" justifyContent='flex-start' flexWrap='wrap' style={{ borderBottom: '2px solid #EEEEEE' }} pb={0.5} mb={1}>
                            <StandardToolbar />
                        </Box>}
                    {/* End: Standard Toolbar - Top */}
                    {/* Custom Toolbar */}
                    {!Boolean(readOnly) && Boolean(mode === 'compact') &&
                        <Box position='absolute' right={10} display='flex' justifyContent='flex-end'>
                            <IconButton size="small" onClick={() => setShowMenu(!showMenu)}>
                                <Icon fontSize="small">{Boolean(showMenu) ? 'expand_less' : 'expand_more'}</Icon>
                            </IconButton>
                            {/* Custom Mode Additional Actions */}
                            <Box display="inline-flex">
                                {Boolean(additionalActions) && additionalActions}
                            </Box>
                            {/* End: Custom Mode Additional Actions */}
                        </Box>}
                    {/* End: Custom Toolbar */}
                    <Typography component="div" variant="body1">
                        <Editable
                            readOnly={Boolean(readOnly)}
                            renderElement={renderElement}
                            renderLeaf={renderLeaf}
                            placeholder="You can input rich text / image / audio / video / Math expression here..."
                            spellCheck
                            autoFocus
                            onKeyDown={event => {
                                for (const hotkey in HOTKEYS) {
                                    if (isHotkey(hotkey, event)) {
                                        event.preventDefault()
                                        const mark = HOTKEYS[hotkey]
                                        toggleMark(editor, mark)
                                    }
                                }
                            }}
                        />
                    </Typography>
                    {/* Toolbar - Bottom */}
                    {!Boolean(readOnly) && (Boolean(mode === 'standard') || Boolean(showMenu)) &&
                        <Box display="flex" justifyContent='flex-start' flexWrap='wrap' style={{ borderTop: '2px solid #EEEEEE' }} pt={0.5} mt={1}>
                            <StandardToolbar />
                        </Box>}
                    {/* End: Toolbar - Bottom */}
                </Slate>
                {/* QR Codes for Media - Print Only */}
                <Box mt={3} display="none" displayPrint="inline-block">
                    {mediaUrls.map((url, uIndex) =>
                        <Box m={1} style={{ display: "inline-block" }}>
                            <Badge badgeContent={uIndex + 1} color="primary" anchorOrigin={{ vertical: "top", horizontal: "left" }}>
                                <QRCode style={{ verticalAlign: "middle" }} value={url} size={80} />
                            </Badge>
                        </Box>
                    )}
                </Box>
                {/* End: QR Codes for Media - Print Only */}
                {Boolean(showWordCount) &&
                    <Box mt={0.5} textAlign="right">
                        <Typography variant="caption" color="textSecondary">
                            {wordCountLabel}
                        </Typography>
                    </Box>}
            </Box>
        </PrintLayoutContext.Provider>
    )
}

const Element = (props) => {
    const { attributes, children, element } = props;
    switch (element.type) {
        case 'block-quote':
            return <blockquote {...attributes}>{children}</blockquote>
        case 'bulleted-list':
            return <ul {...attributes}>{children}</ul>
        case 'heading-one':
            return <h1 {...attributes} style={{ marginTop: 0, marginBottom: '0.75em' }}>{children}</h1>
        case 'heading-two':
            return <h2 {...attributes} style={{ marginTop: 0, marginBottom: '0.75em' }}>{children}</h2>
        case 'list-item':
            return <li {...attributes}>{children}</li>
        case 'numbered-list':
            return <ol {...attributes}>{children}</ol>
        case 'image':
        case 'audio':
            return <MediaElement {...props} />
        case 'link':
            return <LinkElement {...props} />
        case 'video':
            return <VideoElement {...props} />
        case 'math-expression':
            return <MathExpressionElement {...props} />
        case 'paragraph':
            return <p {...attributes} style={PARAGRAPH_BLOCK_STYLE}>{children}</p>
        default:
            return <div {...attributes} style={PARAGRAPH_BLOCK_STYLE}>{children}</div>
    }
}

const Leaf = (props) => {
    const { attributes, children, leaf } = props
    let childrenWithMarks = children;
    if (leaf.bold) {
        childrenWithMarks = <strong>{childrenWithMarks}</strong>
    }
    if (leaf.code) {
        childrenWithMarks = <code>{childrenWithMarks}</code>
    }
    if (leaf.italic) {
        childrenWithMarks = <em>{childrenWithMarks}</em>
    }
    if (leaf.underline) {
        childrenWithMarks = <u>{childrenWithMarks}</u>
    }
    return (
        <React.Fragment>
            {Boolean(leaf.breakBefore) && <br />}
            <span {...attributes}>{childrenWithMarks}</span>
        </React.Fragment>
    );
}

/**
 * Mark Operations
 * TODO: Externalize and centralize
 */
const toggleMark = (editor, format) => {
    const isActive = isMarkActive(editor, format)

    if (isActive) {
        Editor.removeMark(editor, format)
    } else {
        Editor.addMark(editor, format, true)
    }
}

const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor)
    return marks ? marks[format] === true : false
}


